import { operations, type OperationId } from './generated/operations';
import type { Replay } from './lapse';

/**
 * Whether a request held while the session was renewed is sent again (the family rule's replay classes): a read
 * always, an ordinary write when the person renewed while it waited, and never a destructive or irreversible
 * action — publishing, deleting, rotating a key, stopping work, or anything this application confirms with its
 * consequence (D17). Those the person presses again, told so beside the action.
 */
export class Replays {
	static #never = new Set<OperationId>([
		'applications.remove',
		'targets.remove',
		'projects.release',
		'releases.promote',
		'releases.retire',
		'environments.rollback',
		'domains.remove',
		'access.revoke',
		'providers.set',
		'providers.remove',
		'jobs.cancel',
		'platform.plan.set',
		'platform.credit.grant',
		'platform.admissions.set',
		'platform.waitlist.change'
	]);

	/**
	 * @param given What the caller asked for, which wins; `null` asks for no wait at all (the session's own read)
	 */
	static of(id: OperationId, given?: Replay | null): Replay | null {
		if (given !== undefined) return given;
		if (Replays.#never.has(id)) return false;
		return ['GET', 'HEAD'].includes(operations[id].method) ? 'read' : 'write';
	}

	/** A request outside the management contract, by its method */
	static method(method: string): Replay {
		return ['GET', 'HEAD'].includes(method.toUpperCase()) ? 'read' : 'write';
	}
}
